import axios, { AxiosError } from 'axios';

import type {
  Assessment,
  AssessmentHistoryEntry,
  AuditEvent,
  CaseDetail,
  CaseSummary,
  Calculation,
  FactsView,
  FactsVersionSummary,
  ClaimDocument,
  Phase,
  Role,
  SessionUser,
  Slot,
  UploadResult,
} from './types';

export const http = axios.create({
  baseURL: import.meta.env.VITE_API_URL ?? '/api',
  withCredentials: true,
});

/** Surface the API's message rather than "Request failed with status code 400". */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

http.interceptors.response.use(
  (response) => response,
  (error: AxiosError<{ message?: string | string[] }>) => {
    const body = error.response?.data;
    const message = Array.isArray(body?.message)
      ? body.message.join('; ')
      : (body?.message ?? error.message);
    throw new ApiError(message, error.response?.status ?? 0);
  },
);

export const api = {
  login: (email: string, password: string) =>
    http.post<SessionUser>('/auth/login', { email, password }).then((r) => r.data),
  logout: () => http.post<void>('/auth/logout').then(() => undefined),
  me: () => http.get<SessionUser>('/auth/me').then((r) => r.data),
  changePassword: (currentPassword: string, newPassword: string) =>
    http.post<void>('/auth/password', { currentPassword, newPassword }).then(() => undefined),
  createUser: (body: { email: string; name: string; password: string; role: Role }) =>
    http.post<SessionUser>('/auth/users', body).then((r) => r.data),

  phases: () => http.get<Phase[]>('/cases/phases').then((r) => r.data),
  documentTypes: () => http.get<string[]>('/documents/types').then((r) => r.data),

  listCases: (params: { status?: string; outcome?: string; search?: string } = {}) =>
    http
      .get<{ items: CaseSummary[]; total: number }>('/cases', { params })
      .then((r) => r.data),
  getCase: (id: string) => http.get<CaseDetail>(`/cases/${id}`).then((r) => r.data),
  createCase: (body: {
    reference?: string;
    clubNumber?: string;
    claimantName?: string;
    language?: 'de' | 'en';
  }) => http.post<CaseSummary>('/cases', body).then((r) => r.data),

  assess: (id: string) =>
    http.post<{ jobId: string; status: string }>(`/cases/${id}/assess`).then((r) => r.data),
  reassess: (id: string) =>
    http.post<{ jobId: string; status: string }>(`/cases/${id}/reassess`).then((r) => r.data),
  reassessWithNewDocuments: (id: string) =>
    http
      .post<{ jobId: string; status: string }>(`/cases/${id}/documents/reassess`)
      .then((r) => r.data),

  facts: (id: string) => http.get<FactsView>(`/cases/${id}/facts`).then((r) => r.data),
  factsHistory: (id: string) =>
    http.get<FactsVersionSummary[]>(`/cases/${id}/facts/history`).then((r) => r.data),
  correctFacts: (
    id: string,
    body: {
      corrections: Array<{ atom: string; slot: Slot }>;
      note?: string;
      acknowledgeMemberConfirmation?: boolean;
    },
  ) =>
    http
      .post<{ id: string; changedAtoms: string[]; valid: boolean }>(`/cases/${id}/facts`, body)
      .then((r) => r.data),

  assessment: (id: string) => http.get<Assessment>(`/cases/${id}/assessment`).then((r) => r.data),
  assessmentHistory: (id: string) =>
    http.get<AssessmentHistoryEntry[]>(`/cases/${id}/assessments`).then((r) => r.data),
  previewCosts: (id: string, body: unknown) =>
    http.post<Calculation>(`/cases/${id}/costs/preview`, body).then((r) => r.data),
  saveCosts: (id: string, body: unknown) =>
    http.post<Calculation>(`/cases/${id}/costs`, body).then((r) => r.data),
  signOff: (id: string, body: { decision: string; amendedCents?: number; note?: string }) =>
    http.post(`/cases/${id}/sign-off`, body).then((r) => r.data),
  auditTrail: (id: string) => http.get<AuditEvent[]>(`/cases/${id}/audit`).then((r) => r.data),

  documents: (caseId: string) =>
    http.get<ClaimDocument[]>(`/cases/${caseId}/documents`).then((r) => r.data),
  upload: (
    caseId: string,
    files: Array<{ file: File; isPackage: boolean }>,
    onProgress?: (percent: number) => void,
  ) => {
    const form = new FormData();
    for (const item of files) {
      form.append('files', item.file);
      form.append('isPackage', item.isPackage ? 'true' : 'false');
    }
    return http
      .post<UploadResult>(`/cases/${caseId}/documents`, form, {
        onUploadProgress: (event) => {
          if (event.total) onProgress?.(Math.round((event.loaded / event.total) * 100));
        },
      })
      .then((r) => r.data);
  },
  reclassify: (documentId: string, body: Partial<ClaimDocument>) =>
    http.patch<ClaimDocument>(`/documents/${documentId}`, body).then((r) => r.data),
  removeDocument: (documentId: string) =>
    http.delete<void>(`/documents/${documentId}`).then(() => undefined),

  documentUrl: (documentId: string) => `${http.defaults.baseURL}/documents/${documentId}/content`,
  progressUrl: (caseId: string) => `${http.defaults.baseURL}/cases/${caseId}/progress`,
};
